/**
 * Event Manager (FR-EVENTS-01/02/03/07, AC-EVENT-001/003/004).
 *
 * One screen, four jobs:
 *   1. the event table — server-side search, season / type / status filters,
 *      sort by date, name, type, status or season, and paging, all held in the
 *      URL so a refresh or a shared link restores the exact view;
 *   2. create through `EventForm` (a dialog over the table);
 *   3. lifecycle from the row: ONE visible action — the next step (Start,
 *      Close or Reopen, the last two behind consequence-stating dialogs) or
 *      Open — plus a ⋯ menu with Open / Work on this / Delete;
 *   4. the states a list is not finished without: first-run, no-matches,
 *      loading, error-with-retry, and a card layout at 375px (`DataTable`).
 *
 * "Work on this" promotes a row into the session's active-event context, which
 * is the same context the header switcher writes (FR-EVENTS-03/08).
 *
 * RBAC mirrors `services/events`: Team Admin + coaching staff create, edit and
 * close; only administrators reopen and delete (soft delete, behind a two-step
 * confirmation); stat operators get the read-only table.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useActiveEvent } from '../../app/active-event-context';
import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { useToast } from '../../app/toast-context';
import { DataTable, type RowMenuItem } from '../../components/data-table';
import { PageHeader } from '../../components/page-header';
import { ApiError } from '../../lib/api-error';
import { EVENT_STATUSES, EVENT_TYPES, type TeamEvent } from '../../lib/api-types';
import { APP_VIEWS } from '../../lib/constants';
import { EVENT_STATUS_LABELS, EVENT_TYPE_LABELS, seasonYearOptions } from '../../lib/event-format';
import { TableExportButton } from '../integrations/export-dialog';

import { CloseEventDialog, DeleteEventDialog, ReopenEventDialog } from './close-reopen-dialogs';
import { buildEventColumns } from './event-columns';
import { EventForm } from './event-form';
import { buildEventsExportSource } from './events-export';
import {
  canPersona,
  EVENT_CLOSE_PERSONAS,
  EVENT_DELETE_PERSONAS,
  EVENT_REOPEN_PERSONAS,
  EVENT_WRITE_PERSONAS,
  useEventList,
  type EventSortField,
} from './use-events';

type OpenPanel =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'close'; event: TeamEvent }
  | { kind: 'reopen'; event: TeamEvent }
  | { kind: 'delete'; event: TeamEvent };

export function EventManagerPage(): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const { persona, tenantSlug } = useSession();
  const { activeEventId, setActiveEvent, refreshActiveEvent } = useActiveEvent();
  const list = useEventList();
  const navigate = useNavigate();
  const [panel, setPanel] = useState<OpenPanel>({ kind: 'none' });
  const [startingId, setStartingId] = useState<string | null>(null);

  const slug = tenantSlug ?? '';
  const canWrite = canPersona(persona, EVENT_WRITE_PERSONAS);
  const canClose = canPersona(persona, EVENT_CLOSE_PERSONAS);
  const canReopen = canPersona(persona, EVENT_REOPEN_PERSONAS);
  const canDelete = canPersona(persona, EVENT_DELETE_PERSONAS);

  function settle(next: TeamEvent, message: string): void {
    refreshActiveEvent(next);
    setPanel({ kind: 'none' });
    showToast(message, 'success');
    list.reload();
  }

  async function startEvent(event: TeamEvent): Promise<void> {
    setStartingId(event.event_id);
    try {
      settle(
        await apiClient.updateEvent(event.event_id, { status: 'active' }),
        `${event.name} is now active.`
      );
    } catch (caught) {
      showToast(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The event could not be started. Please try again.',
        'error'
      );
    } finally {
      setStartingId(null);
    }
  }

  const openCreate = (): void => setPanel({ kind: 'create' });
  const detailHref = (event: TeamEvent): string =>
    `/app/${slug}/${APP_VIEWS.events}/${event.event_id}`;

  /** One context-appropriate action per row: the next lifecycle step, else Open. */
  function primaryAction(event: TeamEvent): JSX.Element {
    if (canWrite && event.status === 'pending') {
      return (
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={startingId === event.event_id}
          onClick={() => void startEvent(event)}
        >
          {startingId === event.event_id ? 'Starting…' : 'Start'}
        </button>
      );
    }
    if (canClose && event.status === 'active') {
      return (
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => setPanel({ kind: 'close', event })}
        >
          Close
        </button>
      );
    }
    if (canReopen && event.status === 'closed') {
      return (
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => setPanel({ kind: 'reopen', event })}
        >
          Reopen
        </button>
      );
    }
    return (
      <Link className="btn btn--secondary btn--sm" to={detailHref(event)}>
        Open
      </Link>
    );
  }

  function menuFor(event: TeamEvent): RowMenuItem[] {
    const isCurrent = event.event_id === activeEventId;
    const items: RowMenuItem[] = [
      { id: 'open', label: 'Open event', onSelect: () => navigate(detailHref(event)) },
      {
        id: 'work',
        label: 'Work on this',
        isDisabled: isCurrent,
        disabledReason: 'This is already the event you are working on.',
        onSelect: () => {
          setActiveEvent(event);
          showToast(`Now working on ${event.name}.`, 'success');
        },
      },
    ];
    if (canDelete) {
      items.push({
        id: 'delete',
        label: 'Delete',
        tone: 'danger',
        onSelect: () => setPanel({ kind: 'delete', event }),
      });
    }
    return items;
  }

  return (
    <section className="stack event-manager">
      <PageHeader
        title="Event Manager"
        context={
          <>
            {list.total} {list.total === 1 ? 'event' : 'events'} · closed events are in film-review
            mode: only a Team Admin can edit their plays.
          </>
        }
        actions={
          <>
            <TableExportButton
              isDisabled={list.total === 0}
              source={() => buildEventsExportSource(apiClient, list.query)}
            />
            {canWrite ? (
              <button type="button" className="btn btn--primary" onClick={openCreate}>
                Create Event
              </button>
            ) : null}
          </>
        }
      />

      <DataTable<TeamEvent>
        caption="Events"
        surface="events"
        layoutName="Events columns"
        columns={buildEventColumns(slug, activeEventId)}
        rows={list.events}
        getRowId={(event) => event.event_id}
        getRowClassName={(event) =>
          event.event_id === activeEventId ? 'data-table__row--selected' : undefined
        }
        sort={{ by: list.query.sortBy, order: list.query.sortOrder }}
        onSortChange={(sort) =>
          list.setQuery({ sortBy: sort.by as EventSortField, sortOrder: sort.order })
        }
        search={{
          value: list.query.search,
          onChange: (value) => list.setQuery({ search: value }),
          label: 'Search by name or opponent',
          placeholder: 'Search name or opponent',
        }}
        filters={[
          {
            id: 'season',
            label: 'Season',
            value: list.query.season,
            defaultValue: 'all',
            options: [
              { value: 'all', label: 'All seasons' },
              ...seasonYearOptions().map((year) => ({ value: String(year), label: String(year) })),
            ],
            onChange: (value) => list.setQuery({ season: value }),
          },
          {
            id: 'type',
            label: 'Type',
            value: list.query.type,
            defaultValue: 'all',
            options: [
              { value: 'all', label: 'All types' },
              ...EVENT_TYPES.map((type) => ({ value: type, label: EVENT_TYPE_LABELS[type] })),
            ],
            onChange: (value) => list.setQuery({ type: value }),
          },
          {
            id: 'status',
            label: 'Status',
            value: list.query.status,
            defaultValue: 'all',
            options: [
              { value: 'all', label: 'All statuses' },
              ...EVENT_STATUSES.map((status) => ({
                value: status,
                label: EVENT_STATUS_LABELS[status],
              })),
            ],
            onChange: (value) => list.setQuery({ status: value }),
          },
        ]}
        pagination={{
          page: list.query.page,
          pageSize: list.query.pageSize,
          total: list.total,
          onPageChange: (page) => list.setQuery({ page }),
          onPageSizeChange: (pageSize) => list.setQuery({ pageSize }),
        }}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={list.reload}
        emptyTitle={list.isUnfiltered ? 'No events yet' : 'No events match these filters'}
        emptyDescription={
          list.isUnfiltered
            ? 'Create your first game or practice to start logging plays against it.'
            : 'Try a different search term, or clear the season, type and status filters.'
        }
        emptyAction={
          list.isUnfiltered ? (
            canWrite ? (
              <button type="button" className="btn btn--primary btn--sm" onClick={openCreate}>
                Create your first event
              </button>
            ) : null
          ) : (
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() =>
                list.setQuery({ search: '', season: 'all', type: 'all', status: 'all' })
              }
            >
              Clear filters
            </button>
          )
        }
        actionsWidth={124}
        primaryRowAction={primaryAction}
        rowMenu={menuFor}
        getRowMenuLabel={(event) => `More actions for ${event.name}`}
      />

      {panel.kind === 'create' ? (
        <EventForm
          event={null}
          onSave={(input) => apiClient.createEvent(input)}
          onSaved={(saved) => {
            setPanel({ kind: 'none' });
            showToast(`${saved.name} created. Start it when the team takes the field.`, 'success');
            list.reload();
          }}
          onCancel={() => setPanel({ kind: 'none' })}
        />
      ) : null}

      {panel.kind === 'close' ? (
        <CloseEventDialog
          event={panel.event}
          onCancel={() => setPanel({ kind: 'none' })}
          onDone={(next) => settle(next, `${next.name} is closed. Film-review mode is on.`)}
        />
      ) : null}
      {panel.kind === 'reopen' ? (
        <ReopenEventDialog
          event={panel.event}
          onCancel={() => setPanel({ kind: 'none' })}
          onDone={(next) => settle(next, `${next.name} is reopened. Staff can edit plays again.`)}
        />
      ) : null}
      {panel.kind === 'delete' ? (
        <DeleteEventDialog
          event={panel.event}
          onCancel={() => setPanel({ kind: 'none' })}
          onDeleted={(deleted) => {
            if (deleted.event_id === activeEventId) setActiveEvent(null);
            setPanel({ kind: 'none' });
            showToast('Event deleted', 'success');
            list.reload();
          }}
        />
      ) : null}
    </section>
  );
}
